import type { DocumentPageListItem } from '@avisoma//shared';
import { useMemo } from 'react';
import { FaBars } from 'react-icons/fa';
import backend from '../../services/backend';
import AuthGuard from '../AuthGuard';
import { CategoryWithSuggestionApproval } from '../CategoryWithSuggestionApproval';
import { DocumentClassificationDetails } from '../DocumentClassificationDetails';
import { Link } from '../Link';
import { LazyContainer } from '../LazyContainer';
import TagEditor from '../TagEditor';
import { DocumentPageErrorBox } from './DocumentPageErrorBox';
import { DocumentPageStatusIcon } from './DocumentPageStatusIcon';
import { canRetrySegmentation } from '../../lib/canRetrySegmentation';
import { liveSuggestion, resolveClassificationRows } from '../../lib/classificationCategoryResolver';
import { useCategoryCatalogue } from '../../hooks/useCategoryCatalogue';

export interface DocumentPageProcessingSidebarProps {
  documentId: string;
  page: DocumentPageListItem;
  /** Whether this page's action dropdown is the open one. */
  isDropdownOpen: boolean;
  /** Toggle this page's action dropdown (open, or close if already open). */
  onDropdownToggle: (pageId: string) => void;
  requestRelad: () => void;
  strandedStage?: string;
}

export function DocumentPageProcessingSidebar({
  documentId,
  page,
  isDropdownOpen,
  onDropdownToggle,
  requestRelad,
  strandedStage,
}: DocumentPageProcessingSidebarProps) {
  // Hide the downstream stage rows + their Retry actions while segmentation is
  // running or has forked (processing/segmented) — same gate as the page-view
  // ProcessingStatusCard. A parent mid-fork or swapped out for its child
  // segment-pages isn't running those stages, and a retry would race the fork /
  // re-activate a terminal page.
  const segStatus = page.processing?.segmentation?.status;
  const segmentationBlocksDownstream = segStatus === 'processing' || segStatus === 'segmented';

  // DR-3784: the stored ranking snapshots category NAMES, which a rename leaves stale. Resolve
  // the winner against the live catalogue before comparing it with the page's own category —
  // a stale name otherwise reads as a mismatch and paints a permanent warning badge.
  const classification = page.processing?.classification?.result;
  const catalogue = useCategoryCatalogue();
  const classificationRows = useMemo(
    () => resolveClassificationRows(classification, catalogue.page),
    [classification, catalogue.page],
  );
  const liveWinner = liveSuggestion(classificationRows, catalogue.page);

  return (
    <div className="overflow-y-auto flex flex-col gap-4">
      <div className="space-y-2 bg-white dark:bg-zinc-800 p-3 rounded-sm shadow-[0_0_10px_rgba(0,0,0,0.1)] text-gray-500 dark:text-zinc-400 group">
        <div className="flex items-center gap-2 justify-between">
          <div className="overflow-hidden text-ellipsis">
            <AuthGuard
              roles={['admin', 'user', 'curator']}
              fallback={
                <span className="text-sm font-medium">
                  {page.id.slice(0, 10)}...{page.id.slice(-10)}
                </span>
              }
            >
              <Link to={`/document-pages/${page.id}`} className="text-sm font-medium">
                {page.id.slice(0, 10)}...{page.id.slice(-10)}
              </Link>
            </AuthGuard>
          </div>

          {page.mimeType !== 'application/pdf' && (
            <span className="text-xs bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">{page.mimeType}</span>
          )}

          {strandedStage && (
            <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 px-1.5 py-0.5 rounded">
              Stranded: {strandedStage}
            </span>
          )}

          <div className="flex-1" />

          <div className="relative inline-block text-left">
            <button
              type="button"
              aria-label={`Page actions for page ${page.id.slice(0, 10)}`}
              aria-expanded={isDropdownOpen}
              className="h-full inline-flex justify-center items-center w-full rounded-md border border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100 shadow-sm px-3 py-2 bg-white text-lg font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
              onClick={() => onDropdownToggle(page.id)}
            >
              <FaBars className="text-gray-500 dark:text-zinc-400 size-3" />
            </button>
            {isDropdownOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-zinc-800 ring-opacity-5 z-10 ring-1 ring-gray-200 dark:ring-zinc-700">
                <div className="py-2">
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-zinc-100 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
                    onClick={() => {
                      navigator.clipboard.writeText(page.id);
                      onDropdownToggle(page.id);
                    }}
                  >
                    Copy Page ID
                  </button>
                  {canRetrySegmentation(page.processing?.segmentation) && (
                    <AuthGuard roles={['admin', 'curator']}>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
                        onClick={async () => {
                          onDropdownToggle(page.id);
                          await backend.retryDocumentPage(documentId, page.id, 'segmentation');
                          requestRelad();
                        }}
                      >
                        Retry Segmentation
                      </button>
                    </AuthGuard>
                  )}
                  {!segmentationBlocksDownstream && (
                    <AuthGuard roles={['admin', 'curator']}>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
                        onClick={() => {
                          onDropdownToggle(page.id);
                          backend.retryDocumentPage(documentId, page.id, 'pdf-parser');
                        }}
                      >
                        Retry PDF Parser
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
                        onClick={() => {
                          onDropdownToggle(page.id);
                          backend.retryDocumentPage(documentId, page.id, 'metadata');
                        }}
                      >
                        Retry Metadata
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
                        onClick={() => {
                          onDropdownToggle(page.id);
                          backend.pageRetry(page.id, 'data-extraction');
                        }}
                      >
                        Retry Data Extraction
                      </button>
                    </AuthGuard>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {page.parentPageId && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-400 min-w-0">
            <span className="shrink-0">Segment of</span>
            <Link to={`/document-pages/${page.parentPageId}`} className="font-mono truncate">
              {page.parentPageId.slice(0, 10)}...{page.parentPageId.slice(-10)}
            </Link>
          </div>
        )}

        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-100">Category:</label>

          <LazyContainer>
            <CategoryWithSuggestionApproval
              color={page.category && liveWinner && liveWinner !== page.category ? 'warning' : undefined}
              category={page.category}
              classification={classification}
              segmentedChildCount={
                page.processing?.segmentation?.status === 'segmented'
                  ? (page.processing.segmentation.result?.segmentPageIds ?? []).length
                  : undefined
              }
              onSave={async (category) => {
                const response = await backend.updateDocumentPage(page.id, {
                  category,
                });
                // A segmented parent keeps no category of its own — the PATCH
                // redirects the write onto its section children and echoes the
                // applied label back, so stamping it on the parent here would
                // show a category the parent doesn't have (and hide the
                // "Split into N sections" state) until the reload lands.
                if (page.processing?.segmentation?.status !== 'segmented') {
                  page.category = response.data.category;
                }
                requestRelad();
              }}
            />
          </LazyContainer>
        </div>

        <LazyContainer>
          <TagEditor
            tags={page.tags}
            onSave={async (tags) => {
              await backend.updateDocumentPage(page.id, {
                tags,
              });
              requestRelad();
            }}
          />
        </LazyContainer>
      </div>

      <div className="space-y-2 bg-white dark:bg-zinc-800 p-3 rounded-sm shadow-[0_0_10px_rgba(0,0,0,0.1)]">
        {/* Only when the page actually ran segmentation. A child segment-page (it
            IS a segment) and any page processed with ENABLE_SECTION_SEGMENTATION off
            have no segmentation stage — without this guard the icon reads as a
            perpetual "pending". */}
        {page.processing?.segmentation && (
          <>
            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing.segmentation.status} />
                Segmentation
                {page.processing.segmentation.status === 'segmented' && (
                  <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal">
                    (
                    {page.processing.segmentation.result?.segmentCount ??
                      page.processing.segmentation.result?.segmentPageIds?.length ??
                      0}{' '}
                    segments)
                  </span>
                )}
              </span>
            </h4>
            {page.processing.segmentation.status === 'error' && (
              <div>
                <DocumentPageErrorBox error={page.processing.segmentation.errorMessage} />
              </div>
            )}
          </>
        )}

        {!segmentationBlocksDownstream && (
          <>
            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing?.pdfParser?.status} />
                PDF Parser
              </span>
              <AuthGuard roles={['admin', 'user', 'curator']}>
                {page.processing?.pdfParser?.result?.method && (
                  <span className="bg-gray-100 dark:bg-zinc-700 px-2 py-1 rounded-md">
                    {page.processing.pdfParser.result.method}
                  </span>
                )}
              </AuthGuard>
            </h4>
            <div></div>

            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing?.metadata?.status} />
                Metadata
              </span>
            </h4>
            <div>
              {page.processing?.metadata?.status === 'error' && (
                <DocumentPageErrorBox error={page.processing?.metadata?.errorMessage} />
              )}
            </div>

            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing?.embeddings?.status} />
                Embeddings
              </span>
            </h4>
            <div>
              {page.processing?.embeddings?.status === 'error' && (
                <DocumentPageErrorBox error={page.processing?.embeddings?.errorMessage} />
              )}
            </div>

            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing?.classification?.status} />
                Classification
              </span>
            </h4>
            <div>
              {page.processing?.classification?.status === 'done' && (
                <div className="bg-gray-50 dark:bg-zinc-700 rounded border py-1 px-2 mx-4 dark:border-zinc-700">
                  <DocumentClassificationDetails classification={classification} scope="page" />
                </div>
              )}

              {page.processing?.classification?.status === 'error' && (
                <DocumentPageErrorBox error={page.processing?.classification?.errorMessage} />
              )}
            </div>

            <h4 className="text-sm font-medium text-gray-700 dark:text-zinc-100 flex items-center gap-2 justify-between mb-0">
              <span className="flex items-center gap-2">
                <DocumentPageStatusIcon status={page.processing?.dataExtraction?.status} />
                Data Extraction
                {page.processing?.dataExtraction?.category && (
                  <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal">
                    as {page.processing.dataExtraction.category}
                  </span>
                )}
                {page.processing?.dataExtraction?.status === 'skip' && page.processing?.dataExtraction?.skipReason && (
                  <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal">
                    ({page.processing.dataExtraction.skipReason})
                  </span>
                )}
              </span>
            </h4>
            <div>
              {page.processing?.dataExtraction?.status === 'error' && (
                <DocumentPageErrorBox error={page.processing?.dataExtraction?.errorMessage} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
